// Omnix/client/ui/MessagingTemplateEditor.js
import { ComponentBase } from "../core/ComponentBase.js";

export class MessagingTemplateEditor extends ComponentBase {
    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.value = {}; // Will hold the structured data {header, body, footer, buttons}
        this.channelType = 'message'; // Default to the simplest channel
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade';
        this.container.innerHTML = `
            <div class="modal-dialog modal-xl modal-fullscreen-lg-down">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Structured Message Template Editor</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-8">
                                <!-- Main Editor -->
                                <div class="mb-3" id="header-container">
                                    <label class="form-label">Header (Optional)</label>
                                    <input type="text" id="msg-header" class="form-control" placeholder="e.g., Your Order Update">
                                </div>
                                <div class="mb-3">
                                    <label class="form-label">Body</label>
                                    <textarea id="msg-body" class="form-control" rows="6" required placeholder="e.g., Hello {{contact.firstName}}, your order has shipped!"></textarea>
                                </div>
                                <div class="mb-3" id="footer-container">
                                    <label class="form-label">Footer (Optional)</label>
                                    <input type="text" id="msg-footer" class="form-control" placeholder="e.g., Thanks for your business!">
                                </div>
                                <div id="buttons-section-container">
                                    <div id="msg-buttons-container">
                                        <label class="form-label">Buttons (Max 3)</label>
                                    </div>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" id="add-button-btn"><i class="bi bi-plus"></i> Add Button</button>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <!-- Preview -->
                                <h6>Preview</h6>
                                <div class="card">
                                    <div class="card-body" id="preview-pane"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="save-btn">Save Template Body</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.container.querySelector('#save-btn').addEventListener('click', () => this.handleSave());
        this.container.querySelector('#add-button-btn').addEventListener('click', () => this.addButtonRow());
        this.container.addEventListener('input', () => this.updatePreview());
    }

    show(initialData, channelType) {
        this.channelType = channelType || 'message';
        this.setValue(initialData);
        this.updateEditorVisibility(); // NEW: Adapt UI to channel
        this.modalInstance.show();
    }

    hide() {
        this.modalInstance.hide();
    }

    updateEditorVisibility() {
        const header = this.container.querySelector('#header-container');
        const footer = this.container.querySelector('#footer-container');
        const buttons = this.container.querySelector('#buttons-section-container');

        // WhatsApp and Web Chat support all features
        const fullFeatures = ['message'];

        header.style.display = fullFeatures.includes(this.channelType) ? 'block' : 'none';
        footer.style.display = fullFeatures.includes(this.channelType) ? 'block' : 'none';
        buttons.style.display = fullFeatures.includes(this.channelType) ? 'block' : 'none';
    }

    setValue(data) {
        this.value = (typeof data === 'object' && data !== null) ? data : {};
        this.container.querySelector('#msg-header').value = this.value.header || '';
        this.container.querySelector('#msg-body').value = this.value.body || '';
        this.container.querySelector('#msg-footer').value = this.value.footer || '';

        const buttonsContainer = this.container.querySelector('#msg-buttons-container');
        buttonsContainer.innerHTML = '<label class="form-label">Buttons (Max 3)</label>';
        (this.value.buttons || []).forEach(btn => this.addButtonRow(btn));

        this.updatePreview();
    }

    addButtonRow(button = { type: 'QUICK_REPLY', text: '' }) {
        const buttonsContainer = this.container.querySelector('#msg-buttons-container');
        if (buttonsContainer.querySelectorAll('.button-row').length >= 3) {
            this.appClient.notifier.warn("A maximum of 3 buttons is allowed.");
            return;
        }
        const row = document.createElement('div');
        row.className = 'input-group input-group-sm mb-2 button-row';
        row.innerHTML = `
            <input type="text" class="form-control" placeholder="Button Text" value="${button.text}">
            <button class="btn btn-outline-danger" type="button"><i class="bi bi-trash"></i></button>
        `;
        row.querySelector('button').addEventListener('click', () => row.remove());
        buttonsContainer.appendChild(row);
    }

    handleSave() {
        const body = this.container.querySelector('#msg-body').value.trim();
        if (!body) {
            this.appClient.notifier.error("The Body field is required.");
            return;
        }
        const savedData = {
            header: this.container.querySelector('#msg-header').value.trim(),
            body: body,
            footer: this.container.querySelector('#msg-footer').value.trim(),
            buttons: Array.from(this.container.querySelectorAll('.button-row')).map(row => ({
                type: 'QUICK_REPLY', // For now, only support quick reply
                text: row.querySelector('input').value.trim()
            })).filter(btn => btn.text)
        };
        this.emit('saved', savedData);
    }

    updatePreview() {
        // This is a simplified preview. A real one would be more channel-specific.
        const previewPane = this.container.querySelector('#preview-pane');
        const header = this.container.querySelector('#msg-header').value.trim();
        const body = this.container.querySelector('#msg-body').value;
        const footer = this.container.querySelector('#msg-footer').value.trim();
        const buttons = Array.from(this.container.querySelectorAll('.button-row input')).map(input => input.value.trim()).filter(Boolean);

        let previewHtml = '';
        if (header) previewHtml += `<div class="card-header"><strong>${header}</strong></div>`;
        previewHtml += `<div class="card-text p-2">${body.replace(/\n/g, '<br>')}</div>`;
        if (footer) previewHtml += `<div class="card-footer text-muted small">${footer}</div>`;
        if (buttons.length > 0) {
            previewHtml += `<div class="p-2 border-top">${buttons.map(b => `<button class="btn btn-sm btn-outline-primary w-100 mb-1">${b}</button>`).join('')}</div>`;
        }
        previewPane.innerHTML = previewHtml;
    }

    render() {
        return this.container;
    }
}